'use client';

import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  DayPicker,
  type DropdownProps,
  type MonthCaptionProps,
  useDayPicker,
} from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button-variants';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Dropdown({ value, onChange, options }: DropdownProps) {
  const selected = options?.find((option) => option.value === value);

  const handleChange = (newValue: string) => {
    const changeEvent = {
      target: { value: newValue },
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange?.(changeEvent);
  };

  return (
    <Select value={String(value)} onValueChange={handleChange}>
      <SelectTrigger className="h-8 text-sm font-medium gap-1 border-none shadow-none pl-2 pr-1 focus:ring-0 bg-muted/50">
        <SelectValue>{selected?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options?.map((option) => (
          <SelectItem key={option.value} value={String(option.value)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MonthCaption({ children }: MonthCaptionProps) {
  const { goToMonth, previousMonth, nextMonth } = useDayPicker();

  const handlePreviousClick = () => {
    if (previousMonth) {
      goToMonth(previousMonth);
    }
  };

  const handleNextClick = () => {
    if (nextMonth) {
      goToMonth(nextMonth);
    }
  };

  return (
    <div className="flex items-center justify-between gap-1 h-8">
      <Button
        variant="outline"
        className="h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 shrink-0"
        disabled={!previousMonth}
        onClick={handlePreviousClick}
        type="button"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="flex items-center gap-1">
        {children}
      </div>
      <Button
        variant="outline"
        className="h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 shrink-0"
        disabled={!nextMonth}
        onClick={handleNextClick}
        type="button"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  startMonth,
  endMonth,
  numberOfMonths,
  ...props
}: CalendarProps) {
  const currentYear = new Date().getFullYear();
  const defaultStartMonth = startMonth ?? new Date(currentYear - 10, 0);
  const defaultEndMonth = endMonth ?? new Date(currentYear + 10, 11);
  const isMultiMonth = numberOfMonths && numberOfMonths > 1;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout="dropdown"
      startMonth={defaultStartMonth}
      endMonth={defaultEndMonth}
      numberOfMonths={numberOfMonths}
      className={cn('p-3', className)}
      classNames={{
        months: cn('flex gap-4', isMultiMonth ? 'flex-row' : 'flex-col'),
        month: 'flex flex-col gap-2',
        month_caption: 'flex items-center justify-between h-8',
        caption_label: 'hidden',
        dropdowns: 'flex items-center gap-1',
        nav: 'hidden',
        button_previous: 'hidden',
        button_next: 'hidden',
        month_grid: 'w-full border-collapse mt-2',
        weekdays: 'flex',
        weekday: 'text-muted-foreground w-9 font-normal text-[0.8rem] text-center',
        week: 'flex w-full mt-1',
        day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100 cursor-pointer'
        ),
        range_start: 'day-range-start rounded-l-md',
        range_end: 'day-range-end rounded-r-md',
        selected:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md',
        today: 'bg-accent text-accent-foreground rounded-md',
        outside: 'text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground',
        disabled: 'text-muted-foreground opacity-50 cursor-not-allowed',
        range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Dropdown,
        MonthCaption,
        Chevron: ({ orientation }) => {
          if (orientation === 'left') {
            return <ChevronLeft className="h-4 w-4" />;
          }
          return <ChevronRight className="h-4 w-4" />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
